// RUTA: src/components/fitness/CalorieRing.tsx
//
// Anillo circular para nutrición — muestra calorías consumidas vs meta.
// Hermano del CardioRing pero con semántica inversa: mientras que en
// cardio queremos LLENAR el anillo (más kcal quemadas = mejor), en
// nutrición queremos QUEDARNOS DENTRO de la meta (rebasarla = malo).
//
// Filosofía 2024-26 (MacroFactor / Carbon): meta diaria FIJA, sin
// "eat-back" de cardio. Si rebasas se pinta en rojo; cerca está verde.

import { CountUp } from '@/components/ui/CountUp';

interface CalorieRingProps {
  /** Calorías consumidas en el día visualizado */
  consumed: number;
  /** Meta diaria de kcal */
  goal: number;
  size?: number;
  stroke?: number;
  /** Label arriba del número (default "Hoy"). Cuando el usuario navega
   *  a un día pasado, mostramos la fecha corta. */
  label?: string;
}

export function CalorieRing({ consumed, goal, size = 160, stroke = 14, label = 'Hoy' }: CalorieRingProps) {
  const safeGoal = goal > 0 ? goal : 1;
  const pct = (consumed / safeGoal) * 100;
  // Banda saludable: 85-110% de la meta (zona verde)
  const inRange = pct >= 85 && pct <= 110;
  const overGoal = pct > 110;

  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  // Cap visual al 100% — si rebasaste, el anillo se llena pero el color cambia
  const visualPct = Math.min(100, pct);
  const dashOffset = circumference - (visualPct / 100) * circumference;
  const center = size / 2;

  // Verde si en rango, rojo si rebasó, accent si sub-meta
  const progressColor = overGoal
    ? '#ef4444' // red-500: rebasó
    : inRange
      ? '#22c55e' // green-500: en rango
      : '#dc2626'; // accent: aún por debajo
  const trackColor = 'rgba(255,255,255,0.10)';

  const remaining = goal - consumed;
  const stateLabel = overGoal
    ? `+${Math.abs(remaining).toLocaleString()} de más`
    : remaining > 0
      ? `Faltan ${remaining.toLocaleString()}`
      : 'En meta ✓';

  // Anillo "respira" suavemente cuando ya está en banda verde (meta
  // cumplida) — refuerzo visual sin saturar.
  const breathClass = inRange ? 'animate-breath' : '';

  return (
    <div className={`relative inline-flex items-center justify-center ${breathClass}`} style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={center} cy={center} r={radius} fill="none" stroke={trackColor} strokeWidth={stroke} />
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={progressColor}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          style={{ transition: 'stroke-dashoffset 600ms ease-out, stroke 300ms ease-out' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-2">
        <span className="text-[10px] font-bold uppercase tracking-wider text-primary-foreground/60">{label}</span>
        <CountUp
          value={consumed}
          className="text-2xl font-black text-primary-foreground leading-none mt-0.5"
        />
        <span className="text-[10px] font-bold text-primary-foreground/60 mt-0.5">
          / {goal.toLocaleString()} kcal
        </span>
        <span className={`text-[11px] font-black mt-1 ${overGoal ? 'text-red-400' : inRange ? 'text-green-400' : 'text-accent'}`}>
          {Math.round(pct)}%
        </span>
      </div>
    </div>
  );
}

/**
 * Mini-barra para macronutriente (proteína / carbos / grasa). Color
 * por % cumplido de meta:
 *   - verde 85-115%
 *   - amarillo 60-84% o 116-130%
 *   - rojo <60% o >130%
 */
export function MacroBar({
  label,
  current,
  goal,
  color = 'accent',
}: {
  label: string;
  current: number;
  goal: number | null | undefined;
  color?: 'accent' | 'blue' | 'yellow';
}) {
  const hasGoal = goal != null && goal > 0;
  const pct = hasGoal ? (current / goal!) * 100 : 0;
  const visualPct = Math.min(100, pct);

  const fillClass = !hasGoal
    ? 'bg-accent'
    : pct >= 85 && pct <= 115
      ? 'bg-green-500'
      : pct >= 60 && pct <= 130
        ? 'bg-yellow-500'
        : 'bg-red-500';

  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
        <span className="text-[11px] font-bold text-foreground">
          {Math.round(current)}
          {hasGoal && (
            <span className="text-muted-foreground font-normal"> / {goal}g</span>
          )}
          {!hasGoal && <span className="text-muted-foreground font-normal">g</span>}
        </span>
      </div>
      <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${fillClass}`}
          style={{ width: `${visualPct}%` }}
        />
      </div>
    </div>
  );
}
